/**
 * Which uploaded logo files nothing points at any more.
 *
 * A logo is stored on disk the moment it uploads, but only Save writes its
 * address onto the record. So during an edit there can be files on disk that
 * nothing references, and ending the edit — either way — decides which of them
 * are garbage:
 *
 *   Save     the record now holds `committedUrl`; everything uploaded during
 *            the edit that is not that, plus whatever address the record held
 *            before (`supersededUrl`), is unreferenced.
 *   Discard  the form goes back to `committedUrl` (the stored logo, untouched),
 *            and everything uploaded during the edit is unreferenced. Nothing
 *            was superseded, because nothing was written.
 *
 * Extracted when two pages carried this rule copy-pasted and drifting: one of
 * them skipped it on the "nothing but whitespace changed" save path and leaked
 * a file every time. Only OrganizationPage is left (the other page, Branding,
 * was deleted — see settings-open-items.md §9), but the rule is subtle enough
 * to be worth stating once, in one place, with its own tests.
 */
export function orphanedLogoFiles(opts: {
  /** Files uploaded during this edit, in order. */
  uploadedThisEdit: string[];
  /** The address the record holds now (Save), or reverts to (Discard). */
  committedUrl: string | null;
  /** The address the record held before this save. Omit for Discard. */
  supersededUrl?: string | null;
}): string[] {
  const { uploadedThisEdit, committedUrl, supersededUrl } = opts;

  const candidates = [
    ...uploadedThisEdit,
    ...(supersededUrl ? [supersededUrl] : []),
  ];

  const orphans: string[] = [];
  for (const url of candidates) {
    // An in-memory preview, not a stored file — there is nothing to delete, and
    // asking the server to delete a `blob:` address would be nonsense.
    if (!url || url.startsWith('blob:') || url.startsWith('data:')) continue;
    // Still referenced.
    if (url === committedUrl) continue;
    // The same file can arrive twice (uploaded during the edit and also the
    // superseded address); deleting it twice would log a spurious failure.
    if (orphans.includes(url)) continue;
    orphans.push(url);
  }

  return orphans;
}
